"use client";

import { useEffect, useRef, useState } from "react";
import { formatInr } from "pages/GenericOrderDetailsPage/utils/money";
import { useFormContext, useWatch } from "react-hook-form";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useQuery } from "react-query";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setForexCouponInfo } from "redux/features/CreateGICOrder";
import ConfettiWithTimer from "./ConfettiWithTimer";
import { toast } from "sonner";
// Same signature, answered by the new backend. See legacy-order-api.ts.
import { getCalculations } from "service/moneeflo/legacy-order-api";
import { validateForexCoupon } from "service/moneeflo/legacy-forex-desk-api";

/** A code core has checked for this order, and what it takes off in rupees. */
type Coupon = { code: string; discount: number };

// Mock API call to get tax rates
const fetchTaxRates = async () => {
  await new Promise((resolve) => setTimeout(resolve, 300));
  return {
    cgst: 0.09,
    sgst: 0.09,
  };
};

interface CalculationPanelProps {
  grandTotal: number;
  cgst: number;
  sgst: number;
  roundOff: number;
  igst?: number;
  serviceCharge?: number;
  amount?: number;
}

export function CalculationPanel({ preCalcData, order_type }: { preCalcData: CalculationPanelProps | undefined, order_type?: "buy" | "sell" }) {
  const dispatch = useAppDispatch();
  // The panel is rebuilt when the operator steps back to it, so an applied
  // coupon is picked up from where it was kept for the order.
  const kept = useAppSelector((state) => state.CreateGICOrder?.forexCouponInfo) as Partial<Coupon> | null;
  const [couponInfo, setCouponInfo] = useState<Coupon | null>(
    kept?.code ? { code: kept.code, discount: kept.discount ?? 0 } : null,
  );
  const [showConfetti, setShowConfetti] = useState(false);
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [discountCoupon, setDisCountCoupon] = useState<string>("");
  const { control, watch } = useFormContext();
  const customers = useWatch({ control, name: "customers", defaultValue: [] });

  const new_order_type = watch('customers')?.[0]?.products?.[0]?.productType?.type

  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [productBreakdown, setProductBreakdown] = useState<
    Array<{
      label: string;
      currency: string;
      amount: number;
      value: number;
      rate: number;
    }>
  >([]);

  const [calculations, setCalculations] = useState({
    subtotal: 0,
    cgst: 0,
    sgst: 0,
    roundOff: 0,
    grandTotal: 0,
  });

  const { data: taxRates } = useQuery({
    queryKey: ["taxRates"],
    queryFn: fetchTaxRates,
    initialData: { cgst: 0.09, sgst: 0.09 },
  });

  useEffect(() => {
    const allProducts: any[] = [];
    customers.forEach((customer: any) => {
      const products = customer?.products || [];
      products.forEach((product: any) => {
        allProducts.push({
          customer: customer?.fullName || "Unknown",
          product,
        });
      });
    });

    const groupedProducts: Record<
      string,
      {
        label: string;
        currency: string;
        amount: number;
        value: number;
        rate: number;
      }
    > = {};
    // Worked in paise (BigInt), so adding lines never drifts the way floats do.
    const valueMinor: Record<string, bigint> = {};
    const toMinor = (n: number) => BigInt(Math.round(n * 100));

    allProducts.forEach(({ product }) => {
      const productType = product?.productType?.label || "Unknown";
      const currencies = product?.currencies || [];

      currencies.forEach((currency: any) => {
        const currencyCode =
          currency?.currency?.value?.toUpperCase() || "UNKNOWN";
        const amount = currency?.amount || 0;
        const rate = parseFloat(currency?.rate?.toString()) || 1;
        const rateMicros = BigInt(Math.round(rate * 1_000_000));
        const lineMinor = (toMinor(amount) * rateMicros + 500_000n) / 1_000_000n;
        const key = `${currencyCode} ${productType}`;

        if (!groupedProducts[key]) {
          groupedProducts[key] = {
            label: key,
            currency: "INR",
            amount: 0,
            value: 0,
            rate: rate,
          };
          valueMinor[key] = 0n;
        }

        groupedProducts[key].amount += amount;
        valueMinor[key] += lineMinor;
        groupedProducts[key].value = Number(valueMinor[key]) / 100;
      });
    });

    const productList = Object.values(groupedProducts);
    setProductBreakdown(productList);

    const subtotalMinor = Object.values(valueMinor).reduce((sum, v) => sum + v, 0n);
    const taxMinor = (bps: number) => (subtotalMinor * BigInt(Math.round(bps * 10_000)) + 5_000n) / 10_000n;
    const cgstMinor = taxMinor(taxRates?.cgst || 0);
    const sgstMinor = taxMinor(taxRates?.sgst || 0);
    const totalMinor = subtotalMinor + cgstMinor + sgstMinor;
    const roundedMinor = ((totalMinor + 50n) / 100n) * 100n;
    setCalculations({
      subtotal: Number(subtotalMinor) / 100,
      cgst: Number(cgstMinor) / 100,
      sgst: Number(sgstMinor) / 100,
      roundOff: Number(roundedMinor - totalMinor) / 100,
      grandTotal: Number(roundedMinor) / 100,
    });

  }, [customers, taxRates, order_type]);

  const productCount = productBreakdown.length;

  let { data: additionalCharges } = useQuery({
    queryKey: ["additionalCharges", productCount, calculations.subtotal],
    queryFn: () =>
      getCalculations({ amount: calculations.subtotal, type: new_order_type as "buy" | "sell" }),
    enabled: productCount > 0 && !preCalcData,
  });

  if (preCalcData) {
    additionalCharges = {
      CGST: preCalcData.cgst,
      IGST: preCalcData.igst,
      SGST: preCalcData.sgst,
      roundOff: preCalcData.roundOff,
      service_charge: preCalcData?.serviceCharge as number,
      total_payable: {
        grand_total: preCalcData.grandTotal,
      },
      amount: preCalcData.amount as number,
      gst: {
        slab1: 0,
        slab2: 0,
        slab3: 0,
        total_taxable_amount: 0,
        gst: (preCalcData.cgst + preCalcData.sgst) as number
      },
    };
  }

  const isLoadingAdditionalCharges = !additionalCharges && productCount > 0;

  // Calculate current order amount
  const currentOrderAmount = productBreakdown?.reduce(
    (acc: number, item: any) => acc + Number(item?.value || 0),
    0
  );

  const customerId: string | undefined = customers?.[0]?.customerId;

  const dropCoupon = () => {
    setCouponInfo(null);
    setShowConfetti(false);
    dispatch(setForexCouponInfo(null));
  };

  // Core decides whether a code is good for this order and what it takes off:
  // no rule about length, minimum or percentage lives here. Asked when the
  // operator applies it, and again whenever the order's value changes, so the
  // discount shown is always core's figure for what is on screen.
  const couponRequest = useRef(0);
  const checkCoupon = async (code: string, quiet: boolean) => {
    // Only the newest answer counts: an older one arriving late is dropped.
    const mine = ++couponRequest.current;
    setCheckingCoupon(true);
    try {
      const result = await validateForexCoupon({ code, amount: currentOrderAmount, customerId });
      if (mine !== couponRequest.current) return;
      if (!result.valid) {
        dropCoupon();
        const reason = result.reason ?? "This code cannot be used on this order.";
        setCouponMessage(reason);
        if (quiet) toast.error(`Coupon removed: ${reason}`);
        return;
      }
      setCouponMessage(null);
      const coupon = { code: code.trim().toUpperCase(), discount: result.discount };
      setCouponInfo(coupon);
      dispatch(setForexCouponInfo(coupon));
      if (!quiet) {
        setShowConfetti(true);
        toast.success("Coupon applied successfully");
      }
    } catch (err) {
      if (mine !== couponRequest.current) return;
      dropCoupon();
      setCouponMessage(err instanceof Error ? err.message : "The coupon could not be checked.");
    } finally {
      if (mine === couponRequest.current) setCheckingCoupon(false);
    }
  };

  // The order's value changed under an applied coupon: ask core again.
  useEffect(() => {
    if (!couponInfo || !(currentOrderAmount > 0)) return;
    checkCoupon(couponInfo.code, true);
  }, [currentOrderAmount]);

  const handleApplyCoupon = (couponCode: string) => {
    if (couponCode.trim().length < 2 || checkingCoupon) return;
    if (!(currentOrderAmount > 0)) {
      setCouponMessage("Add a product before applying a coupon.");
      return;
    }
    checkCoupon(couponCode, false);
  };

  const discountAmount = couponInfo?.discount ?? 0;

  // Don't render the panel if there are no products
  if (productCount === 0) {
    return (
      <div className="text-center text-[#555555] p-4">
        Please add products to view the calculation details.
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-xl p-6 shadow-[0px_4px_4px_2px_rgba(239,241,251,1)]">
      <div className="flex items-center justify-start gap-2 mb-6">
        <h2 className="text-2xl font-bold ">Calculation</h2>
        <p className="border w-min text-xs capitalize rounded-lg border-blue-700 text-blue-700 px-2 py-0.5">{new_order_type === 'buy' ? 'sale': 'encashment'}</p>
      </div>
      <div className="flex flex-col gap-8">
        {/* Product breakdown */}
        <div className="flex flex-col gap-3">
          {productBreakdown?.map((item, index) => (
            <div key={index}>
              <div className="flex justify-between items-center">
                <span className="font-inter font-medium text-sm leading-none tracking-[-0.04em] text-gray-800">
                  <span className="text-[#666666] text-sm font-normal leading-[112%]">{item?.label}</span> {"  "}{item?.amount} <span className="text-[#5777B2]">@</span> <span className="text-[#666666] text-sm font-normal leading-[112%]">INR</span> {item?.rate}
                </span>
                <div className="flex items-center">
                  <span className="bg-gray-100 text-[#555555] text-xs font-medium px-2 py-0.5 rounded-md mr-2">
                    {item?.currency}
                  </span>
                  <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
                    {item?.value.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
              {index !== productBreakdown.length - 1 && (
                <div className="w-full h-px bg-gray-100" />
              )}
            </div>
          ))}
        </div>


        {/* Additional charges section */}
        <div className="flex flex-col gap-5">
          <div
            className="flex items-center justify-between h-[23px] rounded-[12px] pt-1 pr-3 pb-1 pl-3 bg-gray-50 font-medium cursor-pointer select-none"
            onClick={() => setShowAdditionalCharges(!showAdditionalCharges)}
          >
            <span className="font-medium">Additional charges</span>
            {showAdditionalCharges ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </div>

          {showAdditionalCharges && (
            <div className="space-y-0 mt-2">
              {isLoadingAdditionalCharges ? (
                <div className="text-center text-[#555555] py-4">
                  Loading charges...
                </div>
              ) : !additionalCharges ? (
                <div className="text-center text-[#555555] py-4">
                  No additional charges available
                </div>
              ) : (
                <>
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label="IGST @ 18%"
                    amount={additionalCharges?.IGST as number || 0}
                  />
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label={`CGST @ ${(taxRates?.cgst || 0) * 100}%`}
                    amount={additionalCharges?.CGST}
                  />
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label={`SGST @ ${(taxRates?.sgst || 0) * 100}%`}
                    amount={additionalCharges?.SGST}
                  />
                  <ChargeRow
                    type={new_order_type === 'buy' ? 'sale': 'encashment'}
                    label="Service Charge"
                    amount={additionalCharges?.service_charge}
                  />
                  <ChargeRow
                    label="Round off"
                    amount={additionalCharges?.roundOff}
                  />
                  {discountAmount > 0 && (
                    <ChargeRow
                      label="Discount Coupon"
                      amount={-1 * discountAmount}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* Grand Total */}
          <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff] border-t-2 border-[#176bef] rounded-b-xl">
            <span className="font-inter font-semibold text-base leading-none text-[#1456c4]">
              Grand Total
            </span>
            <div className="flex items-center">
              <span className="bg-[#176bef] text-white-A700 w-[29px] h-[19px] rounded-[8px] px-[6px] flex items-center justify-center">
                <span className="font-inter font-normal text-[10px] leading-[1.12] tracking-[-0.01em]">
                  INR
                </span>
              </span>
              <span className="font-inter font-medium text-base leading-[1.5] text-[#1456c4] ml-3">
                {(additionalCharges?.total_payable?.grand_total || 0) -
                  discountAmount
                  ? (
                    (additionalCharges?.total_payable.grand_total || 0) -
                    discountAmount
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                  : "0.00"}
              </span>
            </div>
          </div>
        </div>
        {
          (new_order_type === 'buy' && !preCalcData?.grandTotal) &&
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 whitespace-nowrap block mb-1">
                Discount Coupon
              </label>
            </div>
            {couponInfo ? (
              <div className="flex items-center justify-between border border-green-500 bg-green-50 px-4 py-3 rounded-[12px] mt-1 relative animate-coupon-drop min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-4 h-4 text-[10px] flex items-center justify-center bg-green-500 text-[#fff] rounded-full shrink-0">
                    ✓
                  </div>
                  <div className="truncate min-w-0">
                    <div className="text-[12px] font-semibold text-green-800 truncate">
                      {couponInfo.code}
                    </div>
                    <div className="text-[10px] text-green-700 truncate">
                      {`Discount (-${formatInr(discountAmount)})`}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    dropCoupon();
                    setDisCountCoupon("");
                    setCouponMessage(null);
                  }}
                  className="w-6 h-6 flex items-center justify-center text-[#fff] bg-purple-400 rounded-full text-[14px] shrink-0"
                  aria-label="Remove coupon"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="relative w-full mt-1">
                <input
                  value={discountCoupon}
                  onChange={(e) => {
                    setDisCountCoupon(e.target.value.toUpperCase());
                    setCouponMessage(null);
                  }}
                  maxLength={40}
                  aria-label="Coupon code"
                  className={`uppercase w-full py-3 pr-[72px] pl-4 text-[12px] text-[#555555] border rounded-[12px] ${false ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                    }`}
                  placeholder="Enter code"
                />
                <button
                  type="button"
                  onClick={() => handleApplyCoupon(discountCoupon)}
                  disabled={checkingCoupon}
                  className="absolute top-[6px] right-[6px] cursor-pointer text-[14px] px-3 py-[6px] rounded-[10px] bg-white text-[#165FE3] font-semibold hover:bg-[#F0F4FF] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Apply
                </button>
                {couponMessage && (
                  <p role="alert" className="mt-1 text-[10px] text-red-700">
                    {couponMessage}
                  </p>
                )}
              </div>
            )}
          </div>
        }
      </div>
      {showConfetti && (
        <ConfettiWithTimer showConfetti={showConfetti} setShowConfetti={setShowConfetti} />
      )}
    </div>
  );
}

export function ChargeRow({ label, amount, type }: { label: string; amount: number; type?: string }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100">
      <span className="font-inter font-medium text-xs leading-none text-gray-700">
        {label}
      </span>
      <div className="flex items-center">
        <span className="bg-gray-100 text-[#555555] text-xs font-medium px-2 py-0.5 rounded-md mr-2">
          INR
        </span>
        <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
          {(type === 'encashment' && amount) ? '-' : ''} {" "} {formatInr(amount).replace("₹", "")}
        </span>
      </div>
    </div>
  );
}
